/**
 * Service-layer wrapper around `/v1/users` (B15-BE).
 *
 * Mirrors `annotationService.ts` conventions:
 *   * `fetchWithAuth` for transport.
 *   * Named exports + aggregator object at the bottom.
 *   * No transformation beyond peeling the envelope (consumers only need
 *     `items`; `total` / `limit` / `offset` are dropped on the floor).
 */

import { fetchWithAuth } from '../../../../../utils/api';
import type { UserSummary, UserSummaryList } from '../types';

const BASE_PATH = '/v1/users';

/**
 * Batch-fetch user summaries by id. Empty input short-circuits to `[]`
 * — mirrors the BE's behaviour without paying for a round-trip.
 *
 * The BE caps the request at 100 ids and silently drops unknown /
 * cross-org ids; callers should treat the response as a possibly-partial
 * lookup and fall back gracefully when an id is missing from the result.
 */
export const getUsersByIds = async (
  ids: string[],
): Promise<UserSummary[]> => {
  if (ids.length === 0) return [];
  const sp = new URLSearchParams();
  sp.append('ids', ids.join(','));
  const envelope: UserSummaryList = await fetchWithAuth(
    `${BASE_PATH}?${sp.toString()}`,
  );
  return envelope.items;
};

/**
 * Aggregator export — mirrors the dual-export convention used by
 * `annotationService.ts` / `datasetsService.ts`.
 */
export const userService = {
  getUsersByIds,
};
